import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Store } from 'lucide-react'

import { createCreativeListing } from '@/api/studio-creative'
import {
  clipOptionsToPublishPayload,
  defaultSelectedCreativePublishKeys,
  listCreativePublishClipOptions,
  type CreativePublishClipOption,
} from '@/features/studio/canvas/creative-publish-clips'
import type { StudioFlowNode } from '@/features/studio/canvas/types'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'

export function CreativeWorkspaceListingModal({
  open,
  onClose,
  workspaceId,
  defaultTitle,
  graphRevision,
  nodes,
  onBeforeSubmit,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  defaultTitle: string
  graphRevision: number
  nodes?: StudioFlowNode[]
  onBeforeSubmit?: () => Promise<boolean>
}) {
  const { t } = useTranslation()
  const clips = useMemo(() => listCreativePublishClipOptions(nodes ?? []), [nodes])
  const [title, setTitle] = useState('')
  const [price, setPrice] = useState('800')
  const [summary, setSummary] = useState('')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle(defaultTitle)
    setPrice('800')
    setSummary('')
    setSelected(defaultSelectedCreativePublishKeys(clips))
    setError(null)
  }, [open, defaultTitle, graphRevision, clips])

  function toggleClip(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function handleSubmit() {
    const parsed = Number(price)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('creative.listing.priceInvalid'))
      return
    }
    if (selected.size === 0) {
      setError(t('creative.listing.selectAtLeastOne'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (onBeforeSubmit) {
        const ok = await onBeforeSubmit()
        if (!ok) {
          setSubmitting(false)
          return
        }
      }
      const res = await createCreativeListing(workspaceId, {
        title: title.trim() || defaultTitle,
        summary: summary.trim(),
        price_points: Math.floor(parsed),
        license_mode: 'non_exclusive',
        clips: clipOptionsToPublishPayload(clips, selected),
      })
      const sellUrl = `/marketplace?tab=sell&listing=${encodeURIComponent(res.listing_public_id)}`
      window.location.assign(sellUrl)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'creative.listing.failed'))
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('creative.listing.title')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('creative.listing.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || clips.length === 0} onClick={() => void handleSubmit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}
            {t('creative.listing.submit')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
      <p className="mb-4 text-xs text-zinc-500">{t('creative.listing.hint')}</p>
      <div className="space-y-3">
        <div>
          <ProtoLabel>{t('creative.listing.fieldTitle')}</ProtoLabel>
          <ProtoInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('creative.listing.fieldPrice')}</ProtoLabel>
          <ProtoInput type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('creative.listing.fieldSummary')}</ProtoLabel>
          <ProtoTextarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} />
        </div>
        <div>
          <ProtoLabel>{t('creative.listing.fieldClips')}</ProtoLabel>
          {clips.length === 0 ? (
            <p className="mt-2 text-sm text-zinc-400">{t('creative.listing.emptyClips')}</p>
          ) : (
            <ul className="mt-2 max-h-[min(320px,40vh)] space-y-2 overflow-y-auto pr-1">
              {clips.map((clip) => (
                <ListingClipRow
                  key={clip.key}
                  clip={clip}
                  checked={selected.has(clip.key)}
                  onToggle={() => toggleClip(clip.key)}
                />
              ))}
            </ul>
          )}
          {clips.length > 0 ? (
            <p className="mt-2 text-xs text-zinc-500">
              {t('creative.listing.selectedCount', { n: selected.size, total: clips.length })}
            </p>
          ) : null}
        </div>
      </div>
    </ProtoModal>
  )
}

function ListingClipRow({
  clip,
  checked,
  onToggle,
}: {
  clip: CreativePublishClipOption
  checked: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const preview = resolveMediaDisplayUrl(clip.previewUrl)

  return (
    <li>
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#2a2a3a] bg-[#1b1b27] px-3 py-2 hover:border-indigo-500/40">
        <Checkbox checked={checked} onCheckedChange={onToggle} />
        <div className="h-12 w-20 shrink-0 overflow-hidden rounded-md bg-black/40">
          {clip.mediaType === 'video' ? (
            <video src={preview} className="h-full w-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-zinc-100">{clip.label}</p>
          <p className="text-xs text-zinc-500">
            {clip.mediaType === 'video' ? t('creative.publish.mediaVideo') : t('creative.publish.mediaImage')}
          </p>
        </div>
      </label>
    </li>
  )
}
